import React, { useEffect, useRef, useContext } from "react";
import fileContext from "../../context/fileContext";

import "../../styles/PdfEditor_styles.css";

import {
  PdfViewerComponent,
  Toolbar,
  Magnification,
  Navigation,
  LinkAnnotation,
  BookmarkView,
  ThumbnailView,
  Print,
  TextSelection,
  Annotation,
  TextSearch,
  FormFields,
  FormDesigner,
  Inject,
} from "@syncfusion/ej2-react-pdfviewer";
import { auth, storage } from "../../helper/firebaseClient";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import useAuth from "../../stores/authStore";
import { api } from "../../utils/axios-instance";

const CACHE_NAME = "blob-cache";

const PdfEditor = ({ preUrl, fileId, fileName, setSaving }) => {
  const viewerRef = useRef(null);
  const Editorhieght = window.innerHeight - 60;
  const profileData = useAuth((state) => state.profileData);
  const context = useContext(fileContext);

  // Fetch and open the pdf
  useEffect(() => {
    const fetchAndExtractContent = async (pdfFileUrl) => {
      try {
        // Fetch the pdf file
        const response = await fetch(pdfFileUrl);
        const pdfBlob = await response.blob();
        const pdfFile = new File([pdfBlob], fileName, {
          type: "application/pdf",
        });

        // Convert data to base64
        const base64data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(pdfBlob);
          reader.onloadend = () => resolve(reader.result.split(",")[1]);
          reader.onerror = (error) => reject(error);
        });

        // Wait for the viewer to be ready before loading the PDF
        if (viewerRef.current) {
          setTimeout(() => {
            const viewer = document.getElementById(`pdf-container-${fileId}`)
              ?.ej2_instances[0];

            if (viewer) {
              viewer.load("data:application/pdf;base64," + base64data, null);
              if (typeof context.setCurrFileFor === "function") {
                context.setCurrFileFor(fileId, pdfFile);
              }
              context.setCurrFile(pdfFile);
            } else {
              console.error("Viewer is not initialized.");
            }
          }, 1500);
        }
      } catch (error) {
        console.error("Error while fetching the pdf file: ", error);
      }
    };

    if (preUrl) {
      fetchAndExtractContent(preUrl);
    }
  }, [preUrl, fileName]);

  var saveButton = {
    id: "savetofirebase",
    text: "Save",
    tooltipText: "Save to Cloud",
    align: "right",
  };

  async function toolbarClick(args) {
    const viewer = document.getElementById(`pdf-container-${fileId}`)
      ?.ej2_instances[0];

    if (viewer) {
      if (args.item && args.item.id === "savetofirebase") {
        setSaving(true);
        const blob = await viewer.saveAsBlob(fileName);
        const file = new File([blob], fileName, { type: "application/pdf" });
        // Save to firebase — always the canonical object path (fileId),
        // never a version id. Keeps bytes and the storage pointer in sync.

        const fileRef = ref(storage, `files/${profileData.org_id}/${fileId}`);
        const metadata = {
          customMetadata: {
            department_id: profileData.dept,
            org_id: profileData.org_id,
          },
        };

        const uploadTask = uploadBytesResumable(fileRef, file, metadata);

        uploadTask.on(
          "state_changed",
          null,
          (error) => {
            console.error("File upload error:", error);
            // Always clear the saving flag so the UI never hangs on a failed save.
            setSaving(false);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log("File available at:", downloadURL);

            const token = await auth.currentUser.getIdToken();
            const fileIdentifier = `${fileId}-${token}`;

            // Refresh download_url server-side. Overwriting a GCS object rotates
            // its token, so the previously stored download_url is now stale.
            try {
              await api.post(`file/update-file-version/${fileId}/`, {
                download_url: downloadURL,
                idToken: token,
              });
            } catch (err) {
              // Non-fatal — metadata update failure does not stop editing.
              console.warn(
                "update-file-version failed (non-fatal):",
                err?.response?.status,
                err?.response?.data
              );
            }

            // Bust the read cache so the next open re-fetches fresh bytes.
            const cache = await caches.open(CACHE_NAME);
            await cache.delete(fileIdentifier);

            setSaving(false);
          }
        );
      }
    }
  }

  return (
    <div style={{ height: "80vh", width: "100%" }}>
      <PdfViewerComponent
        id={`pdf-container-${fileId}`}
        ref={viewerRef}
        resourceUrl="https://cdn.syncfusion.com/ej2/26.2.11/dist/ej2-pdfviewer-lib"
        height="100%"
        width="100%"
        enablePrint={true}
        toolbarClick={toolbarClick}
        toolbarSettings={{
          showTooltip: true,
          toolbarItems: [
            saveButton,
            "OpenOption",
            "PageNavigationTool",
            "MagnificationTool",
            "PanTool",
            "SelectionTool",
            "SearchOption",
            "PrintOption",
            "DownloadOption",
            "UndoRedoTool",
            "AnnotationEditTool",
            "FormDesignerEditTool",
            "CommentTool",
            "SubmitForm",
            "OrganizePagesTool",
          ],
        }}
      >
        <Inject
          services={[
            Toolbar,
            Magnification,
            Navigation,
            Annotation,
            LinkAnnotation,
            BookmarkView,
            ThumbnailView,
            Print,
            TextSelection,
            TextSearch,
            FormFields,
            FormDesigner,
          ]}
        />
      </PdfViewerComponent>
    </div>
  );
};

export default PdfEditor;